import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { NotificationProvider } from "./context/NotificationContext";
import { Toaster } from "./components/ui/toaster";
import { PushPrompt } from "./components/PushPrompt";

// Components
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
// ChatPanel now lives inside Navbar as a dropdown
import InstallPrompt from "./components/core/InstallPrompt";
import RankUpWatcher from "./components/RankUpWatcher";
// Pages
import LandingPage from "./pages/LandingPage";
import SignupPage from "./pages/SignupPage";
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import FeedPage from "./pages/FeedPage";
import PostDetailPage from "./pages/PostDetailPage";
import PostEditorPage from "./pages/PostEditorPage";
import MessagesPage from "./pages/MessagesPage";
import TiersPage from "./pages/TiersPage";
import ProfilePage from "./pages/ProfilePage";
import AdminPage from "./pages/AdminPage";
import SiteSettingsPage from "./pages/SiteSettingsPage";
import CollectionsPage from "./pages/CollectionsPage";
import PublicCollectionsPage from "./pages/PublicCollectionsPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import PaymentAdminPage from "./pages/PaymentAdminPage";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import ContentPolicyPage from "./pages/ContentPolicyPage";
import HelpCentrePage from "./pages/HelpCentrePage";
import RankPreviewPage from "./pages/RankPreviewPage";
import RanksPage from "./pages/RanksPage";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { currentUser, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Public Route Component (allows logged in users to view landing page too)
const PublicRoute = ({ children }) => {
  const { loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  // Allow everyone to view public pages (landing page accessible even when logged in)
  return children;
};

function AppContent() {
  const { currentUser } = useAuth();

  return (
    <div className="App min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1">
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={
            <PublicRoute>
              <LandingPage />
            </PublicRoute>
          } />
          <Route path="/signup" element={
            <PublicRoute>
              <SignupPage />
            </PublicRoute>
          } />
          <Route path="/login" element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          } />
          <Route path="/forgot-password" element={
            <PublicRoute>
              <ForgotPasswordPage />
            </PublicRoute>
          } />
          <Route path="/reset-password" element={
            <PublicRoute>
              <ResetPasswordPage />
            </PublicRoute>
          } />
          <Route path="/tiers" element={<TiersPage />} />

          {/* Public Store Route (browsable by all logged-in users) */}
          <Route path="/store" element={
            <ProtectedRoute>
              <PublicCollectionsPage />
            </ProtectedRoute>
          } />
          <Route path="/collections" element={
            <ProtectedRoute>
              <PublicCollectionsPage />
            </ProtectedRoute>
          } />

          {/* Protected Routes */}
          <Route path="/feed" element={
            <ProtectedRoute>
              <FeedPage />
            </ProtectedRoute>
          } />
          <Route path="/post/:postId" element={
            <ProtectedRoute>
              <PostDetailPage />
            </ProtectedRoute>
          } />
          <Route path="/messages" element={
            <ProtectedRoute>
              {currentUser?.reviewerMode ? <Navigate to="/feed" replace /> : <MessagesPage />}
            </ProtectedRoute>
          } />
          <Route path="/profile" element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          } />
          <Route path="/admin" element={
            <ProtectedRoute>
              <AdminPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/site-settings" element={
            <ProtectedRoute>
              <SiteSettingsPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/collections" element={
            <ProtectedRoute>
              <CollectionsPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/posts/new" element={
            <ProtectedRoute>
              <PostEditorPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/posts/:postId/edit" element={
            <ProtectedRoute>
              <PostEditorPage />
            </ProtectedRoute>
          } />

          {/* Payment */}
          <Route path="/payment/success" element={
            <ProtectedRoute>
              <PaymentSuccessPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/payments" element={
            <ProtectedRoute>
              <PaymentAdminPage />
            </ProtectedRoute>
          } />

          {/* Preview / Demo Pages */}
          <Route path="/ranks" element={
            <ProtectedRoute>
              <RanksPage />
            </ProtectedRoute>
          } />
          <Route path="/preview/ranks" element={
            <ProtectedRoute>
              <RankPreviewPage />
            </ProtectedRoute>
          } />

          {/* Legal Pages */}
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/content-policy" element={<ContentPolicyPage />} />
          <Route path="/help-centre" element={<HelpCentrePage />} />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <Footer />
      <InstallPrompt />
      {currentUser && <RankUpWatcher key={currentUser.id} />}
      <Toaster />
      <PushPrompt />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <NotificationProvider>
          <AppContent />
        </NotificationProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
